/**
 * Duvar 3D view (three.js). Renders the wall and the picture behind it, animates exits and bumps,
 * turns taps into block ids and lets the player tilt the camera to peek. The picture hangs GAP
 * behind the wall and closed columns are backed by dark mortar, so nothing leaks at the start,
 * but through an opened hole a tilt shows part of the neighbouring picture (parallax).
 * Budget (Galaxy A14 class, 60 FPS): ≤ 75 blocks in one InstancedMesh, markers in two more,
 * one textured plane, two lights, no shadows, renders only when something moved.
 * The rules live in core/game.ts; this file never decides what a tap does.
 */
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { DIRS, type DuvarLevel } from "./core/game";

const STONE = [0xf1ebdf, 0xd9d1c2, 0xbdb4a3];
const MARK = 0x262b4d;
const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
const MAX_YAW = 0.5;
const MAX_PITCH = 0.38;
/** Distance between the back of the wall and the picture: what a tilt can see through a hole. */
const GAP = 1.2;
const BACK = -0.5; // back face of layer 0

interface Anim {
  id: number;
  t0: number;
  dur: number;
  kind: "exit" | "bump";
  dir: readonly [number, number, number];
}

function hash01(i: number): number {
  let h = Math.imul(i + 0x9e37, 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function arrowShape(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(-0.26, 0.07);
  s.lineTo(0.04, 0.07);
  s.lineTo(0.04, 0.2);
  s.lineTo(0.3, 0);
  s.lineTo(0.04, -0.2);
  s.lineTo(0.04, -0.07);
  s.lineTo(-0.26, -0.07);
  s.closePath();
  return s;
}

export class DuvarView {
  readonly canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
  private blocks: THREE.InstancedMesh;
  private arrows: THREE.InstancedMesh;
  private dots: THREE.InstancedMesh;
  private picture: THREE.Mesh;
  private mortar: THREE.InstancedMesh;
  private base: THREE.Vector3[] = [];
  private gone: boolean[] = [];
  private anims: Anim[] = [];
  private flashUntil = new Map<number, number>();
  private yaw = 0;
  private pitch = 0;
  private dist = 10;
  private dirty = true;
  private raf = 0;
  private raycaster = new THREE.Raycaster();
  private down: { x: number; y: number; yaw: number; pitch: number; moved: boolean; id: number } | null = null;
  /** True while the player holds a tilt (the camera drifts back to the front view on release). */
  private holdTilt = false;
  private frames = 0;
  private fpsT0 = performance.now();
  fps = 0;

  constructor(
    private host: HTMLElement,
    private level: DuvarLevel,
    private onTap: (id: number) => void,
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.canvas = this.renderer.domElement;
    this.canvas.setAttribute("aria-hidden", "true");
    host.append(this.canvas);

    const { w, h, d } = level;
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x2a2f55, 1.15));
    const sun = new THREE.DirectionalLight(0xffffff, 1.6);
    sun.position.set(-4, 6, 9);
    this.scene.add(sun);

    // A shadow box: back panel, the picture on it, four side walls up to the wall's back face.
    const dark = new THREE.MeshStandardMaterial({ color: 0x12152b, roughness: 0.95 });
    const pz = BACK - GAP;
    const panel = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, h + 0.6, 0.2), dark);
    panel.position.set(0, 0, pz - 0.11);
    this.picture = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: 0x30365f }));
    this.picture.position.set(0, 0, pz);
    const side = (sx: number, sy: number, x: number, y: number) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, GAP), dark);
      m.position.set(x, y, pz + GAP / 2);
      return m;
    };
    this.scene.add(panel, this.picture, side(0.3, h + 0.6, -w / 2 - 0.15, 0), side(0.3, h + 0.6, w / 2 + 0.15, 0), side(w, 0.3, 0, h / 2 + 0.15), side(w, 0.3, 0, -h / 2 - 0.15));
    // Mortar behind every closed column (hidden when the column opens).
    this.mortar = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ color: 0x0c0e1f }), w * h);
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) this.mortar.setMatrixAt(y * w + x, new THREE.Matrix4().makeTranslation(x - (w - 1) / 2, y - (h - 1) / 2, BACK + 0.01));
    this.scene.add(this.mortar);

    const n = level.blocks.length;
    const box = new RoundedBoxGeometry(0.96, 0.96, 0.96, 2, 0.08);
    this.blocks = new THREE.InstancedMesh(box, new THREE.MeshStandardMaterial({ roughness: 0.72, metalness: 0 }), n);
    const arrowGeo = new THREE.ShapeGeometry(arrowShape());
    this.arrows = new THREE.InstancedMesh(arrowGeo, new THREE.MeshBasicMaterial({ color: MARK }), n);
    const dotGeo = new THREE.RingGeometry(0.12, 0.22, 24);
    this.dots = new THREE.InstancedMesh(dotGeo, new THREE.MeshBasicMaterial({ color: MARK }), n);
    const color = new THREE.Color();
    for (const b of level.blocks) {
      this.base[b.id] = new THREE.Vector3(b.x - (w - 1) / 2, b.y - (h - 1) / 2, b.z);
      this.gone[b.id] = false;
      color.setHex(STONE[Math.min(STONE.length - 1, d - 1 - b.z)]!).multiplyScalar(0.94 + hash01(b.id) * 0.08);
      this.blocks.setColorAt(b.id, color);
    }
    this.scene.add(this.blocks, this.arrows, this.dots);
    for (const b of level.blocks) this.place(b.id, this.base[b.id]!);
    this.flush();

    this.canvas.addEventListener("pointerdown", this.onDown);
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
    this.resize();
    this.loop();
  }

  /** Picture texture: the HD twin when it loads, otherwise the pixel art (canvas). */
  setPicture(source: HTMLImageElement | HTMLCanvasElement): void {
    const { w, h } = this.level;
    const cv = document.createElement("canvas");
    cv.width = cv.height = 512;
    const ctx = cv.getContext("2d")!;
    ctx.fillStyle = "#f4efe6";
    ctx.fillRect(0, 0, 512, 512);
    ctx.imageSmoothingEnabled = source instanceof HTMLImageElement;
    ctx.drawImage(source, 0, 0, 512, 512);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    // Cover the w×h wall with the square picture (crop the long side evenly).
    if (w > h) {
      tex.repeat.set(1, h / w);
      tex.offset.set(0, (1 - h / w) / 2);
    } else if (h > w) {
      tex.repeat.set(w / h, 1);
      tex.offset.set((1 - w / h) / 2, 0);
    }
    const mat = this.picture.material as THREE.MeshBasicMaterial;
    mat.map?.dispose();
    mat.map = tex;
    mat.color.setHex(0xffffff);
    mat.needsUpdate = true;
    this.dirty = true;
  }

  private place(id: number, p: THREE.Vector3, scale = 1): void {
    const b = this.level.blocks[id]!;
    const m = new THREE.Matrix4().compose(p, new THREE.Quaternion(), new THREE.Vector3(scale, scale, scale));
    this.blocks.setMatrixAt(id, m);
    // Marker just in front of the block's front face (0.48): coplanar would z-fight when tilted.
    const face = p.clone().add(new THREE.Vector3(0, 0, 0.495 * scale));
    const angle = b.dir === 0 ? 0 : b.dir === 1 ? Math.PI : b.dir === 2 ? Math.PI / 2 : -Math.PI / 2;
    const mark = new THREE.Matrix4().compose(face, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), angle), new THREE.Vector3(scale, scale, scale));
    this.arrows.setMatrixAt(id, b.dir === 4 ? HIDDEN : mark);
    this.dots.setMatrixAt(id, b.dir === 4 ? mark : HIDDEN);
  }

  private flush(): void {
    this.blocks.instanceMatrix.needsUpdate = true;
    this.arrows.instanceMatrix.needsUpdate = true;
    this.dots.instanceMatrix.needsUpdate = true;
    if (this.blocks.instanceColor) this.blocks.instanceColor.needsUpdate = true;
    this.dirty = true;
  }

  /** Slide a block out along its arrow, then hide it. */
  exit(id: number): void {
    const b = this.level.blocks[id]!;
    this.gone[id] = true;
    this.anims.push({ id, t0: performance.now(), dur: b.dir === 4 ? 260 : 340, kind: "exit", dir: DIRS[b.dir]! });
    this.dirty = true;
  }

  /** Nudge toward the arrow and back; flash the block in the way. */
  bump(id: number, blocker: number): void {
    const b = this.level.blocks[id]!;
    this.anims.push({ id, t0: performance.now(), dur: 220, kind: "bump", dir: DIRS[b.dir]! });
    if (blocker >= 0) this.flashUntil.set(blocker, performance.now() + 320);
    this.dirty = true;
  }

  /** A picture column opened: drop its mortar once the last block has flown out. */
  openColumn(x: number, y: number): void {
    window.setTimeout(() => {
      this.mortar.setMatrixAt(y * this.level.w + x, HIDDEN);
      this.mortar.instanceMatrix.needsUpdate = true;
      this.dirty = true;
    }, 200);
  }

  /** Everything still standing leaves at once (after a correct guess or giving up). */
  clearAll(ids: number[]): void {
    ids.forEach((id, i) => window.setTimeout(() => this.exit(id), i * 18));
    window.setTimeout(() => {
      for (let i = 0; i < this.level.w * this.level.h; i++) this.mortar.setMatrixAt(i, HIDDEN);
      this.mortar.instanceMatrix.needsUpdate = true;
      this.dirty = true;
    }, ids.length * 18 + 200);
  }

  resize(): void {
    const r = this.host.getBoundingClientRect();
    const width = Math.max(1, r.width);
    const height = Math.max(1, r.height);
    this.renderer.setSize(width, height, false);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.camera.aspect = width / height;
    const { w, h, d } = this.level;
    const vfov = THREE.MathUtils.degToRad(this.camera.fov);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * this.camera.aspect);
    // Fit the wall's front face (the largest thing on screen) with a slim margin.
    this.dist = Math.max((w / 2 + 0.35) / Math.tan(hfov / 2), (h / 2 + 0.35) / Math.tan(vfov / 2)) + d / 2;
    this.camera.updateProjectionMatrix();
    this.dirty = true;
  }

  private updateCamera(): void {
    const c = this.camera;
    c.position.set(Math.sin(this.yaw) * Math.cos(this.pitch) * this.dist, Math.sin(this.pitch) * this.dist, Math.cos(this.yaw) * Math.cos(this.pitch) * this.dist);
    c.lookAt(0, 0, (this.level.d - 1) / 2);
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const now = performance.now();
    this.frames++;
    if (now - this.fpsT0 >= 1000) {
      this.fps = Math.round((this.frames * 1000) / (now - this.fpsT0));
      this.frames = 0;
      this.fpsT0 = now;
    }
    let moving = this.anims.length > 0 || this.flashUntil.size > 0;
    if (!this.down && (Math.abs(this.yaw) > 0.001 || Math.abs(this.pitch) > 0.001) && !this.holdTilt) {
      // A released tilt drifts slowly back to the front view.
      this.yaw *= 0.92;
      this.pitch *= 0.92;
      moving = true;
    }
    if (!moving && !this.dirty) return;
    const color = new THREE.Color();
    for (const a of [...this.anims]) {
      const t = Math.min(1, (now - a.t0) / a.dur);
      const base = this.base[a.id]!;
      if (a.kind === "exit") {
        const k = t * t * t;
        const reach = a.dir[2] ? 4 : Math.max(this.level.w, this.level.h) + 1.5;
        const p = base.clone().add(new THREE.Vector3(a.dir[0] * reach * k, a.dir[1] * reach * k, a.dir[2] * reach * k));
        this.place(a.id, p, 1 - 0.85 * k);
        if (t >= 1) {
          this.blocks.setMatrixAt(a.id, HIDDEN);
          this.arrows.setMatrixAt(a.id, HIDDEN);
          this.dots.setMatrixAt(a.id, HIDDEN);
        }
      } else {
        const k = Math.sin(t * Math.PI) * 0.2;
        this.place(a.id, base.clone().add(new THREE.Vector3(a.dir[0] * k, a.dir[1] * k, a.dir[2] * k)));
      }
      if (t >= 1) this.anims.splice(this.anims.indexOf(a), 1);
    }
    for (const [id, until] of this.flashUntil) {
      const b = this.level.blocks[id]!;
      const base = new THREE.Color(STONE[Math.min(STONE.length - 1, this.level.d - 1 - b.z)]!);
      if (now >= until) {
        this.blocks.setColorAt(id, base);
        this.flashUntil.delete(id);
      } else this.blocks.setColorAt(id, color.setHex(0xff595e).lerp(base, 1 - (until - now) / 320));
    }
    this.flush();
    this.updateCamera();
    this.renderer.render(this.scene, this.camera);
    this.dirty = false;
  };

  private pick(clientX: number, clientY: number): number {
    const r = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = this.raycaster.intersectObject(this.blocks, false)[0];
    const id = hit?.instanceId ?? -1;
    return id >= 0 && !this.gone[id] ? id : -1;
  }

  private onDown = (e: PointerEvent): void => {
    this.down = { x: e.clientX, y: e.clientY, yaw: this.yaw, pitch: this.pitch, moved: false, id: this.pick(e.clientX, e.clientY) };
    this.holdTilt = true;
  };

  private onMove = (e: PointerEvent): void => {
    const d = this.down;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 10) return;
    d.moved = true;
    const r = this.canvas.getBoundingClientRect();
    this.yaw = THREE.MathUtils.clamp(d.yaw - (dx / r.width) * 1.6, -MAX_YAW, MAX_YAW);
    this.pitch = THREE.MathUtils.clamp(d.pitch + (dy / r.height) * 1.4, -MAX_PITCH, MAX_PITCH);
    this.dirty = true;
  };

  private onUp = (): void => {
    const d = this.down;
    this.down = null;
    this.holdTilt = false;
    if (d && !d.moved && d.id >= 0) this.onTap(d.id);
  };

  /** Page coordinates of a block's front face (bot hook / smoke tests). */
  screenPoint(id: number): { x: number; y: number } {
    this.updateCamera();
    const p = this.base[id]!.clone().add(new THREE.Vector3(0, 0, 0.47)).project(this.camera);
    const r = this.canvas.getBoundingClientRect();
    return { x: r.left + ((p.x + 1) / 2) * r.width, y: r.top + ((1 - p.y) / 2) * r.height };
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
      for (const mat of Array.isArray(m) ? m : m ? [m] : []) {
        (mat as THREE.MeshBasicMaterial).map?.dispose();
        mat.dispose();
      }
    });
    this.renderer.dispose();
    this.canvas.remove();
  }
}
